import { useQuery } from '@apollo/client';
import type { NumberString } from '@openx/types/helpers';
import { useSnackbar } from 'notistack';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GetOrganizationsOptionsDocument,
  type GetOrganizationsOptionsQuery,
  type GetOrganizationsOptionsQueryVariables,
} from '../../../graphql/query/organizations/GetOrganizationsOptions.generated';
export type OrganizationInfo = Record<NumberString, { id: number; name: string } | undefined>;

export const useOrganizationsByIdsFetch = (organizationIds: number[]) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const { data, loading, error } = useQuery<GetOrganizationsOptionsQuery, GetOrganizationsOptionsQueryVariables>(
    GetOrganizationsOptionsDocument,
    {
      variables: {
        where: {
          exchange_account_partner_id: { _in: organizationIds.map(String) },
        },
      },
      skip: organizationIds.length === 0,
      onError: () => {
        enqueueSnackbar(t('Failed to fetch organization details'), { variant: 'error' });
      },
    }
  );

  const organizations = useMemo<OrganizationInfo>(() => {
    if (!data?.account_list) {
      return {};
    }

    const orgMap: OrganizationInfo = {};

    data.account_list.forEach((org: GetOrganizationsOptionsQuery['account_list'][0]) => {
      if (org?.exchange_account_partner_id) {
        orgMap[org.exchange_account_partner_id] = {
          id: org.exchange_account_partner_id,
          name: org.name,
        };
      }
    });

    return orgMap;
  }, [data]);

  return {
    organizations,
    isLoading: loading,
    error: error || null,
  };
};
